import { UserCheck } from 'lucide-react';
import { type ReactNode, useContext, useState } from 'react';

import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    buildCaregiverSignalMatcher,
    CaregiverMemorySignalSection,
    CaregiverNameButton,
    CaregiverSignalChips,
    getCaregiverMatchSources,
} from '@apps/web/components/phoebe_chat_view/tool_calls/caregiver_memory_tool_ui';
import { splitShiftDatetime } from '@apps/web/components/phoebe_chat_view/tool_calls/shift_datetime';
import {
    CaregiverToolErrorCard,
    formatCaregiverSignalLabel,
    formatTimestamp,
    formatToolFieldLabel,
    getRecordArray,
    getStringArray,
    IdentifierRedactionContext,
    TOOL_SURFACE_HOVER_CLASS,
    ToolDetailHeader,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    isUuidOnlyValue,
    redactUuidsInText,
} from '@apps/web/components/phoebe_chat_view/tool_calls/uuid_redaction';
import { formatPhoneNumber } from '@apps/web/lib/contact_utils';
import { cn } from '@apps/web/lib/utils';

const SEARCH_RESULT_CARD_LIMIT = 4;
const SEARCH_FILTER_SKIP_KEYS = new Set<string>([
    'limit',
    'verbose',
    'verbosity',
]);

type SearchFilterToken = {
    readonly key: string;
    readonly label: string;
};

function getSearchFilterTokens(
    args: Record<string, unknown> | Array<unknown> | null,
    redact: boolean,
): Array<SearchFilterToken> {
    if (!args || Array.isArray(args)) {
        return [];
    }

    const tokens: Array<SearchFilterToken> = [];
    for (const [key, value] of Object.entries(args)) {
        if (
            SEARCH_FILTER_SKIP_KEYS.has(key) ||
            value === null ||
            value === undefined ||
            value === '' ||
            // Search chips are always visible (no expand), so a raw
            // `caregiver_id` filter would put a UUID on screen unprompted.
            (redact && isUuidOnlyValue(value))
        ) {
            continue;
        }

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (redact && isUuidOnlyValue(item)) {
                    return;
                }
                const itemLabel = formatSearchFilterValue(item, redact);
                if (itemLabel) {
                    tokens.push({
                        key: `${key}-${index}-${itemLabel}`,
                        label: `${formatToolFieldLabel(key)}: ${itemLabel}`,
                    });
                }
            });
            continue;
        }

        const label = formatSearchFilterValue(value, redact);
        if (label) {
            tokens.push({
                key,
                label: `${formatToolFieldLabel(key)}: ${label}`,
            });
        }
    }

    return tokens;
}

function formatSearchFilterValue(
    value: unknown,
    redact: boolean,
): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0) {
            return null;
        }
        return redact ? redactUuidsInText(trimmed) : trimmed;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (!isRecord(value)) {
        return null;
    }

    const from = getStringValue(value, 'from');
    const to = getStringValue(value, 'to');
    if (from || to) {
        return [from ? `from ${from}` : null, to ? `to ${to}` : null]
            .filter(Boolean)
            .join(', ');
    }

    return null;
}

function SearchFilterSection({
    args,
}: {
    readonly args: Record<string, unknown> | Array<unknown> | null;
}) {
    const redact = useContext(IdentifierRedactionContext);
    const tokens = getSearchFilterTokens(args, redact);
    if (tokens.length === 0) {
        return null;
    }

    return (
        <ToolTopicSection
            title="Filters"
            className="border-blue-200 bg-blue-50/60 shadow-sm dark:border-blue-900/50 dark:bg-blue-950/30"
        >
            <div className="flex flex-wrap gap-1.5">
                {tokens.map((token) => (
                    <ToolToken key={token.key} tone="blue">
                        {token.label}
                    </ToolToken>
                ))}
            </div>
        </ToolTopicSection>
    );
}

function formatSearchCountLabel({
    count,
    singular,
    plural,
    emptyLabel,
}: {
    readonly count: number;
    readonly singular: string;
    readonly plural: string;
    readonly emptyLabel: string;
}) {
    if (count === 0) {
        return emptyLabel;
    }
    return `${count} ${count === 1 ? singular : plural} found`;
}

export function SearchResultsHeader({
    countLabel,
    query,
}: {
    readonly countLabel: string;
    readonly query?: string | null;
}) {
    return (
        <div className="border-border/60 bg-muted/30 rounded-lg border px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-foreground text-sm font-semibold">
                    {countLabel}
                </p>
                {query ? <ToolToken tone="blue">{query}</ToolToken> : null}
            </div>
        </div>
    );
}

function SearchResultsMessage({
    message,
}: {
    readonly message: string | null;
}) {
    if (!message) {
        return null;
    }

    return (
        <ToolTopicSection title="Note">
            <p className="text-foreground text-xs">{message}</p>
        </ToolTopicSection>
    );
}

function SearchResultsMoreButton({
    hiddenCount,
    label,
    onClick,
}: {
    readonly hiddenCount: number;
    readonly label: string;
    readonly onClick: () => void;
}) {
    if (hiddenCount <= 0) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className="text-primary hover:text-primary/80 cursor-pointer text-left text-[10px] font-medium"
        >
            +{hiddenCount} more {label}
        </button>
    );
}

function getSearchCount(
    data: Record<string, unknown>,
    rows: ReadonlyArray<unknown>,
): number {
    return getNumberValue(data, 'count') ?? rows.length;
}

function SearchResultCard({ children }: { readonly children: ReactNode }) {
    return (
        <div
            data-testid="search-result-card"
            className={cn(
                'border-border/60 bg-card rounded-lg border p-3',
                TOOL_SURFACE_HOVER_CLASS,
            )}
        >
            {children}
        </div>
    );
}

function AssignedCaregiverPanel({
    caregiverId,
    caregiverName,
    onCaregiverClick,
}: {
    readonly caregiverId: string | null;
    readonly caregiverName: string;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    return (
        <div className="flex items-center gap-2 py-1">
            <UserCheck className="text-muted-foreground h-3.5 w-3.5 flex-shrink-0" />
            <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-[10px] font-medium tracking-wide uppercase">
                    Assigned caregiver
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <CaregiverNameButton
                        caregiverId={caregiverId}
                        name={caregiverName}
                        onCaregiverClick={onCaregiverClick}
                        className="text-sm font-semibold"
                    />
                    <ToolToken tone="green">Assigned</ToolToken>
                </div>
            </div>
        </div>
    );
}

export function CaregiverSearchCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllCaregivers, setShowAllCaregivers] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Caregiver search needs review"
                error={error}
            />
        );
    }

    const caregivers = getRecordArray(data['caregivers']);
    const count = getSearchCount(data, caregivers);
    const visibleCaregivers = showAllCaregivers
        ? caregivers
        : caregivers.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenCaregiverCount = caregivers.length - visibleCaregivers.length;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={formatSearchCountLabel({
                    count,
                    singular: 'caregiver',
                    plural: 'caregivers',
                    emptyLabel: 'No caregivers found',
                })}
            />
            <SearchFilterSection args={args} />
            <SearchResultsMessage message={getStringValue(data, 'hint')} />
            {visibleCaregivers.map((caregiver) => {
                const caregiverId = getStringValue(caregiver, 'caregiver_id');
                const caregiverName =
                    getStringValue(caregiver, 'name') ?? 'Caregiver';
                const roles = getStringArray(caregiver['roles']);
                const skills = getStringArray(caregiver['skills']);
                const tags = getStringArray(caregiver['tags']);
                const hasSignalFields =
                    roles.length > 0 || skills.length > 0 || tags.length > 0;
                const officeName = getStringValue(caregiver, 'office_name');
                const primaryPhone = getStringValue(caregiver, 'primary_phone');
                const primaryEmail = getStringValue(caregiver, 'primary_email');
                const isActive = caregiver['is_active'] === true;
                const matcher = buildCaregiverSignalMatcher(
                    args,
                    ...getCaregiverMatchSources(caregiver),
                );

                return (
                    <SearchResultCard key={caregiverId ?? caregiverName}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <CaregiverNameButton
                                    caregiverId={caregiverId}
                                    name={caregiverName}
                                    onCaregiverClick={onCaregiverClick}
                                    className="text-sm font-semibold"
                                />
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                    {officeName ? (
                                        <span>{officeName}</span>
                                    ) : null}
                                    {primaryPhone ? (
                                        <span>
                                            {formatPhoneNumber(primaryPhone)}
                                        </span>
                                    ) : null}
                                    {primaryEmail ? (
                                        <span>{primaryEmail}</span>
                                    ) : null}
                                </div>
                            </div>
                            <ToolToken tone={isActive ? 'green' : 'neutral'}>
                                {isActive ? 'Active' : 'Inactive'}
                            </ToolToken>
                        </div>
                        {hasSignalFields ? (
                            <div className="mt-3 space-y-2">
                                <CaregiverMemorySignalSection
                                    label="Roles"
                                    values={roles}
                                    matcher={matcher}
                                />
                                <CaregiverMemorySignalSection
                                    label="Skills"
                                    values={skills}
                                    matcher={matcher}
                                />
                                <CaregiverMemorySignalSection
                                    label="Notes"
                                    values={tags}
                                    matcher={matcher}
                                />
                            </div>
                        ) : null}
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenCaregiverCount}
                label="caregivers"
                onClick={() => setShowAllCaregivers(true)}
            />
        </div>
    );
}

function getCaregiverAttributeCount(
    counts: Record<string, unknown> | null,
    groupKey: string,
    value: string,
): number | null {
    const rawGroupCounts = counts?.[groupKey];
    const groupCounts = isRecord(rawGroupCounts) ? rawGroupCounts : null;
    return getNumberValue(groupCounts, value);
}

function CaregiverAttributeSummarySection({
    title,
    values,
    counts,
    countKey,
}: {
    readonly title: string;
    readonly values: ReadonlyArray<string>;
    readonly counts: Record<string, unknown> | null;
    readonly countKey: string;
}) {
    const rows = values.map((value) => ({
        value,
        label: formatCaregiverSignalLabel(value),
        count: getCaregiverAttributeCount(counts, countKey, value),
    }));
    const maxCount = Math.max(...rows.map((row) => row.count ?? 0), 0);

    if (values.length === 0) {
        return (
            <ToolTopicSection title={title}>
                <p className="text-muted-foreground text-xs">None returned</p>
            </ToolTopicSection>
        );
    }

    if (maxCount === 0) {
        return (
            <ToolTopicSection title={title}>
                <div className="flex flex-wrap gap-1">
                    {rows.map((row) => (
                        <ToolToken key={row.value}>{row.label}</ToolToken>
                    ))}
                </div>
            </ToolTopicSection>
        );
    }

    return (
        <ToolTopicSection title={title}>
            <div className="space-y-1.5">
                {rows.map((row) => {
                    const widthPercent =
                        row.count !== null
                            ? Math.max(
                                  8,
                                  Math.round((row.count / maxCount) * 100),
                              )
                            : 0;
                    return (
                        <div
                            key={row.value}
                            data-testid={`caregiver-attribute-row-${row.value}`}
                            className="group bg-muted/35 rounded-md border border-transparent px-2 py-1.5 transition-transform duration-150 ease-out hover:-translate-y-0.5 hover:scale-[1.01] hover:border-blue-200/70 hover:bg-blue-50/45 hover:shadow-sm hover:shadow-blue-950/5 dark:hover:border-blue-800/60 dark:hover:bg-blue-950/30"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-foreground min-w-0 truncate text-xs font-medium">
                                    {row.label}
                                </span>
                                {row.count !== null ? (
                                    <ToolToken tone="blue">
                                        {row.count}
                                    </ToolToken>
                                ) : null}
                            </div>
                            {row.count !== null ? (
                                <div className="bg-border/60 mt-1 h-1 rounded-full transition-all duration-150 group-hover:h-1.5 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/50">
                                    <div
                                        data-testid={`caregiver-attribute-bar-${row.value}`}
                                        className="h-full rounded-full bg-blue-500 transition-all duration-200 ease-out group-hover:bg-blue-600 group-hover:shadow-[0_0_0_3px_rgba(37,99,235,0.14)]"
                                        style={{
                                            width: `${widthPercent}%`,
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </ToolTopicSection>
    );
}

export function CaregiverAttributeSummaryCards({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const caregiverCount = getNumberValue(data, 'caregiver_count');
    const counts = isRecord(data['counts']) ? data['counts'] : null;
    const roles = getStringArray(data['roles']);
    const tags = getStringArray(data['tags']);
    const preferenceTags =
        getStringArray(data['preference_tags']).length > 0
            ? getStringArray(data['preference_tags'])
            : getStringArray(data['preferences_tags']);
    const includeInactive = args?.['include_inactive'] === true;
    const groupSummaries = [
        roles.length > 0
            ? `${roles.length} role${roles.length === 1 ? '' : 's'}`
            : null,
        tags.length > 0
            ? `${tags.length} tag${tags.length === 1 ? '' : 's'}`
            : null,
        preferenceTags.length > 0
            ? `${preferenceTags.length} preference${preferenceTags.length === 1 ? '' : 's'}`
            : null,
    ].filter((summary): summary is string => summary !== null);
    const headerBadges: Array<{
        readonly label: string;
        readonly tone?: 'blue' | 'green' | 'neutral' | 'red';
    }> = [];
    if (includeInactive) {
        headerBadges.push({ label: 'Includes inactive' });
    }
    if (counts) {
        headerBadges.push({ label: 'Counts included', tone: 'blue' });
    }

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={
                    caregiverCount !== null
                        ? `${caregiverCount} caregiver${caregiverCount === 1 ? '' : 's'} summarized`
                        : 'Caregiver attributes summarized'
                }
                subtitle={groupSummaries.join(', ')}
                badges={headerBadges}
            />
            <CaregiverAttributeSummarySection
                title="Roles"
                values={roles}
                counts={counts}
                countKey="roles"
            />
            <CaregiverAttributeSummarySection
                title="Qualification tags"
                values={tags}
                counts={counts}
                countKey="tags"
            />
            <CaregiverAttributeSummarySection
                title="Preference tags"
                values={preferenceTags}
                counts={counts}
                countKey="preference_tags"
            />
        </div>
    );
}

export function ClientSearchCards({
    data,
    onClientClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onClientClick?: (clientId: string) => void;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllClients, setShowAllClients] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Client search needs review"
                error={error}
            />
        );
    }

    const clients = getRecordArray(data['clients']);
    const count = getSearchCount(data, clients);
    const visibleClients = showAllClients
        ? clients
        : clients.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenClientCount = clients.length - visibleClients.length;
    const query =
        getStringValue(data, 'query') ?? getStringValue(args, 'query');

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={formatSearchCountLabel({
                    count,
                    singular: 'client',
                    plural: 'clients',
                    emptyLabel: 'No clients found',
                })}
                query={query}
            />
            <SearchFilterSection args={args} />
            <SearchResultsMessage message={getStringValue(data, 'hint')} />
            {visibleClients.map((client) => {
                const clientId = getStringValue(client, 'client_id');
                const name = getStringValue(client, 'name') ?? 'Client';
                const city = getStringValue(client, 'city');
                const state = getStringValue(client, 'state');
                const address =
                    getStringValue(client, 'address_line_1') ??
                    getStringValue(client, 'address');
                const officeName = getStringValue(client, 'office_name');
                const phone = getStringValue(client, 'phone_number');
                const email = getStringValue(client, 'email');
                const tags = getStringArray(client['tags']);
                const carePlanSummary =
                    getStringValue(client, 'care_plan_summary') ??
                    getStringValue(client, 'care_plan');
                const location = [city, state].filter(Boolean).join(', ');
                const matcher = buildCaregiverSignalMatcher(
                    args,
                    query,
                    carePlanSummary,
                );

                return (
                    <SearchResultCard key={clientId ?? name}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                {clientId && onClientClick ? (
                                    <button
                                        type="button"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onClientClick(clientId);
                                        }}
                                        className="text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left text-sm font-semibold underline underline-offset-2"
                                    >
                                        {name}
                                    </button>
                                ) : (
                                    <p className="text-foreground text-sm font-semibold">
                                        {name}
                                    </p>
                                )}
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                    {location ? <span>{location}</span> : null}
                                    {officeName ? (
                                        <span>{officeName}</span>
                                    ) : null}
                                </div>
                            </div>
                            {client['is_active'] === false ? (
                                <ToolToken>Inactive</ToolToken>
                            ) : null}
                        </div>
                        <div className="mt-3 space-y-2">
                            {address ? (
                                <ToolTopicSection title="Address">
                                    <p className="text-foreground text-xs">
                                        {address}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {phone || email ? (
                                <ToolTopicSection title="Contact">
                                    <div className="text-foreground space-y-0.5 text-xs">
                                        {phone ? (
                                            <p>{formatPhoneNumber(phone)}</p>
                                        ) : null}
                                        {email ? <p>{email}</p> : null}
                                    </div>
                                </ToolTopicSection>
                            ) : null}
                            {carePlanSummary ? (
                                <ToolTopicSection title="Care plan">
                                    <p className="text-foreground line-clamp-3 text-xs">
                                        {carePlanSummary}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {tags.length > 0 ? (
                                <ToolTopicSection title="Tags">
                                    <CaregiverSignalChips
                                        values={tags}
                                        matcher={matcher}
                                    />
                                </ToolTopicSection>
                            ) : null}
                        </div>
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenClientCount}
                label="clients"
                onClick={() => setShowAllClients(true)}
            />
        </div>
    );
}

export function ShiftSearchCards({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllShifts, setShowAllShifts] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Shift search needs review"
                error={error}
            />
        );
    }
    const shifts = getRecordArray(data['shifts']);
    const count = getSearchCount(data, shifts);
    const visibleShifts = showAllShifts
        ? shifts
        : shifts.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenShiftCount = shifts.length - visibleShifts.length;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={formatSearchCountLabel({
                    count,
                    singular: 'shift',
                    plural: 'shifts',
                    emptyLabel: 'No shifts found',
                })}
            />
            <SearchFilterSection args={args} />
            <SearchResultsMessage
                message={
                    getStringValue(data, 'message') ??
                    getStringValue(data, 'hint')
                }
            />
            {visibleShifts.map((shift) => {
                const shiftId = getStringValue(shift, 'shift_id');
                const patientName =
                    getStringValue(shift, 'patient_name') ?? 'Unknown patient';
                const start = splitShiftDatetime(
                    getStringValue(shift, 'start_time'),
                );
                const end = splitShiftDatetime(
                    getStringValue(shift, 'end_time'),
                );
                const shiftDay =
                    getStringValue(shift, 'shift_day') ?? start.day;
                // Keep the end date visible when the shift crosses
                // midnight.
                const startTime = start.time;
                const endTime =
                    end.day && end.day !== start.day
                        ? `${end.day} ${end.time}`
                        : end.time;
                const city = getStringValue(shift, 'city');
                const caregiverId =
                    getStringValue(shift, 'caregiver_id') ??
                    getStringValue(shift, 'assigned_caregiver_id');
                const caregiverName =
                    getStringValue(shift, 'caregiver_name') ??
                    getStringValue(shift, 'assigned_caregiver_name');
                const status = getStringValue(shift, 'status');
                const roleRequired = getStringValue(shift, 'role_required');
                const skillsRequired = getStringArray(shift['skills_required']);
                const timeLabel =
                    startTime || endTime
                        ? `${startTime ?? ''}${startTime && endTime ? ' - ' : ''}${endTime ?? ''}`
                        : null;
                const requirementValues = [
                    roleRequired,
                    ...skillsRequired,
                ].filter((value): value is string => Boolean(value));
                const matcher = buildCaregiverSignalMatcher(
                    args,
                    requirementValues,
                );

                return (
                    <SearchResultCard key={shiftId ?? patientName}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-foreground text-sm font-semibold">
                                    {patientName}
                                </p>
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                    {shiftDay ? <span>{shiftDay}</span> : null}
                                    {timeLabel ? (
                                        <span>{timeLabel}</span>
                                    ) : null}
                                    {city ? <span>{city}</span> : null}
                                </div>
                            </div>
                            {status ? (
                                <ToolToken>
                                    {formatCaregiverSignalLabel(status)}
                                </ToolToken>
                            ) : null}
                        </div>
                        <div className="mt-3 space-y-2">
                            <ToolTopicSection title="Assignment">
                                <div>
                                    {caregiverName ? (
                                        <AssignedCaregiverPanel
                                            caregiverId={caregiverId}
                                            caregiverName={caregiverName}
                                            onCaregiverClick={onCaregiverClick}
                                        />
                                    ) : (
                                        <ToolToken tone="red">
                                            Unassigned
                                        </ToolToken>
                                    )}
                                </div>
                            </ToolTopicSection>
                            {requirementValues.length > 0 ? (
                                <ToolTopicSection title="Requirements">
                                    <CaregiverSignalChips
                                        values={requirementValues}
                                        matcher={matcher}
                                    />
                                </ToolTopicSection>
                            ) : null}
                        </div>
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenShiftCount}
                label="shifts"
                onClick={() => setShowAllShifts(true)}
            />
        </div>
    );
}

export function OutreachSearchCards({
    data,
    resultKind,
}: {
    readonly data: Record<string, unknown>;
    readonly resultKind: 'open-shift' | 'outreach';
}) {
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllOutreach, setShowAllOutreach] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Outreach search needs review"
                error={error}
            />
        );
    }

    const outreaches = getRecordArray(data['callouts']);
    const count = getSearchCount(data, outreaches);
    const totalCount = getNumberValue(data, 'total_count');
    const visibleOutreaches = showAllOutreach
        ? outreaches
        : outreaches.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenOutreachCount = outreaches.length - visibleOutreaches.length;
    const countLabel =
        totalCount !== null && totalCount > count
            ? `${count} of ${totalCount} outreach items shown`
            : formatSearchCountLabel({
                  count,
                  singular:
                      resultKind === 'open-shift'
                          ? 'open shift'
                          : 'outreach item',
                  plural:
                      resultKind === 'open-shift'
                          ? 'open shifts'
                          : 'outreach items',
                  emptyLabel:
                      resultKind === 'open-shift'
                          ? 'No open shifts found'
                          : 'No outreach found',
              });

    return (
        <div className="space-y-2">
            <SearchResultsHeader countLabel={countLabel} />
            <SearchFilterSection args={args} />
            <SearchResultsMessage
                message={
                    getStringValue(data, 'message') ??
                    getStringValue(data, 'hint')
                }
            />
            {visibleOutreaches.map((outreach) => {
                const outreachId =
                    getStringValue(outreach, 'callout_id') ??
                    getStringValue(outreach, 'outreach_id');
                const patientName =
                    getStringValue(outreach, 'patient_name') ??
                    'Name not included';
                const status = getStringValue(outreach, 'status');
                const outreachType = getStringValue(outreach, 'outreach_type');
                const shiftDay = getStringValue(outreach, 'shift_day');
                const startTime = getStringValue(outreach, 'start_time');
                const endTime = getStringValue(outreach, 'end_time');
                const shiftCount =
                    getNumberValue(outreach, 'shift_count') ??
                    getRecordArray(outreach['shifts']).length;
                const attemptCount =
                    getNumberValue(outreach, 'contact_attempts') ?? 0;
                const timeLabel =
                    startTime || endTime
                        ? `${startTime ?? ''}${startTime && endTime ? ' - ' : ''}${endTime ?? ''}`
                        : null;
                const shifts = getRecordArray(outreach['shifts']);

                return (
                    <SearchResultCard key={outreachId ?? patientName}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-foreground text-sm font-semibold">
                                    {patientName}
                                </p>
                                <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                    {shiftDay ? <span>{shiftDay}</span> : null}
                                    {timeLabel ? (
                                        <span>{timeLabel}</span>
                                    ) : null}
                                    <span>
                                        {attemptCount} attempt
                                        {attemptCount === 1 ? '' : 's'}
                                    </span>
                                    {shiftCount > 0 ? (
                                        <span>
                                            {shiftCount} shift
                                            {shiftCount === 1 ? '' : 's'}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-1">
                                {status ? (
                                    <ToolToken>
                                        {formatCaregiverSignalLabel(status)}
                                    </ToolToken>
                                ) : null}
                                {outreachType ? (
                                    <ToolToken>
                                        {formatCaregiverSignalLabel(
                                            outreachType,
                                        )}
                                    </ToolToken>
                                ) : null}
                            </div>
                        </div>
                        {shifts.length > 0 ? (
                            <div className="mt-3 space-y-1">
                                {shifts.slice(0, 2).map((shift) => {
                                    const shiftId = getStringValue(
                                        shift,
                                        'shift_id',
                                    );
                                    const shiftPatient =
                                        getStringValue(shift, 'patient_name') ??
                                        patientName;
                                    const shiftDayLabel = getStringValue(
                                        shift,
                                        'shift_day',
                                    );
                                    const shiftStart = getStringValue(
                                        shift,
                                        'start_time',
                                    );
                                    const shiftEnd = getStringValue(
                                        shift,
                                        'end_time',
                                    );
                                    const shiftStatus = getStringValue(
                                        shift,
                                        'status',
                                    );
                                    const shiftTime =
                                        shiftStart || shiftEnd
                                            ? `${shiftStart ?? ''}${shiftStart && shiftEnd ? ' - ' : ''}${shiftEnd ?? ''}`
                                            : null;

                                    return (
                                        <div
                                            key={shiftId ?? shiftPatient}
                                            className="border-border/60 bg-background rounded-md border p-2"
                                        >
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="text-foreground text-xs font-medium">
                                                    {shiftPatient}
                                                </span>
                                                {shiftDayLabel ? (
                                                    <span className="text-muted-foreground text-xs">
                                                        {shiftDayLabel}
                                                    </span>
                                                ) : null}
                                                {shiftTime ? (
                                                    <span className="text-muted-foreground text-xs">
                                                        {shiftTime}
                                                    </span>
                                                ) : null}
                                                {shiftStatus ? (
                                                    <ToolToken>
                                                        {formatCaregiverSignalLabel(
                                                            shiftStatus,
                                                        )}
                                                    </ToolToken>
                                                ) : null}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : null}
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenOutreachCount}
                label="outreach items"
                onClick={() => setShowAllOutreach(true)}
            />
        </div>
    );
}

export function ConversationHistorySearchCards({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllMessages, setShowAllMessages] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Conversation search needs review"
                error={error}
            />
        );
    }

    const results = getRecordArray(data['results']);
    const count = getSearchCount(data, results);
    const query =
        getStringValue(data, 'query') ?? getStringValue(args, 'query');
    const visibleResults = showAllMessages
        ? results
        : results.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenMessageCount = results.length - visibleResults.length;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={formatSearchCountLabel({
                    count,
                    singular: 'message',
                    plural: 'messages',
                    emptyLabel: 'No messages found',
                })}
                query={query}
            />
            <SearchFilterSection args={args} />
            {visibleResults.map((result) => {
                const messageId = getStringValue(result, 'message_id');
                const role = getStringValue(result, 'role');
                const source = getStringValue(result, 'source');
                const createdAt = getStringValue(result, 'created_at');
                const content = getStringValue(result, 'content');

                return (
                    <SearchResultCard key={messageId ?? content ?? 'message'}>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {role ? (
                                <ToolToken>
                                    {formatCaregiverSignalLabel(role)}
                                </ToolToken>
                            ) : null}
                            {source ? (
                                <ToolToken>{source.toUpperCase()}</ToolToken>
                            ) : null}
                            {createdAt ? (
                                <span className="text-muted-foreground text-[11px]">
                                    {formatTimestamp(createdAt, timezone)}
                                </span>
                            ) : null}
                        </div>
                        {content ? (
                            <p className="text-foreground mt-2 text-xs">
                                {content}
                            </p>
                        ) : null}
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenMessageCount}
                label="messages"
                onClick={() => setShowAllMessages(true)}
            />
        </div>
    );
}

export function VoiceTranscriptSearchCards({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { timezone } = usePhoebeChatOrganizationScope();
    const args = isRecord(data['__tool_args']) ? data['__tool_args'] : null;
    const [showAllTranscripts, setShowAllTranscripts] = useState(false);
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title="Voice transcript search needs review"
                error={error}
            />
        );
    }

    const results = getRecordArray(data['results']);
    const count = getSearchCount(data, results);
    const query =
        getStringValue(data, 'query') ?? getStringValue(args, 'query');
    const visibleResults = showAllTranscripts
        ? results
        : results.slice(0, SEARCH_RESULT_CARD_LIMIT);
    const hiddenTranscriptCount = results.length - visibleResults.length;

    return (
        <div className="space-y-2">
            <SearchResultsHeader
                countLabel={formatSearchCountLabel({
                    count,
                    singular: 'transcript',
                    plural: 'transcripts',
                    emptyLabel: 'No transcripts found',
                })}
                query={query}
            />
            <SearchFilterSection args={args} />
            {visibleResults.map((result) => {
                const voiceCallId = getStringValue(result, 'voice_call_id');
                const startedAt = getStringValue(result, 'started_at');
                const fromNumber = getStringValue(result, 'from_number');
                const toNumber = getStringValue(result, 'to_number');
                const formattedFromNumber = formatPhoneNumber(fromNumber);
                const formattedToNumber = formatPhoneNumber(toNumber);
                const callSummary = getStringValue(result, 'call_summary');
                const transcript = getStringValue(result, 'transcript');

                return (
                    <SearchResultCard
                        key={voiceCallId ?? transcript ?? 'voice-call'}
                    >
                        <div className="text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                            {startedAt ? (
                                <span>
                                    {formatTimestamp(startedAt, timezone)}
                                </span>
                            ) : null}
                            {formattedFromNumber ? (
                                <span>From {formattedFromNumber}</span>
                            ) : null}
                            {formattedToNumber ? (
                                <span>To {formattedToNumber}</span>
                            ) : null}
                        </div>
                        <div className="mt-3 space-y-2">
                            {callSummary ? (
                                <ToolTopicSection title="Call Summary">
                                    <p className="text-foreground text-xs">
                                        {callSummary}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {transcript ? (
                                <ToolTopicSection title="Transcript">
                                    <p className="text-foreground text-xs">
                                        {transcript}
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                        </div>
                    </SearchResultCard>
                );
            })}
            <SearchResultsMoreButton
                hiddenCount={hiddenTranscriptCount}
                label="transcripts"
                onClick={() => setShowAllTranscripts(true)}
            />
        </div>
    );
}
